import { useError } from 'src/hooks/useError'
import { unique } from 'src/libs/helper'
import { UserTranslator } from 'src/translators'
import {
  CreateExternalUserFormEntity,
  CreateInternalUserFormEntity,
  ErrorEntity,
  InternalUserEntity,
  UserAssignProjectFormEntity,
  UserEntity,
} from 'src/types'
import { CompassRoleEntity } from 'src/types/entities/CompassRole'
import {
  useAddUserMutation,
  useUpdateWorksRoleMutation,
  useDeleteUserMutation,
  useAddExternalUserMutation,
  useAssignUserProjectsMutation,
  useAssignUsersProjectsMutation,
  useUpdateInternalUserNameMutation,
  useUpdateUserKeywordsMutation,
  useUpdateUserCompanyRolesMutation,
} from 'src/types/generated'

type CreateUserInput = {
  formEntity: CreateInternalUserFormEntity
}
type UpdateRoleInput = {
  userId: number
  roleId: number
}
type DeleteUserInput = {
  userId: number
}

type CreateExternalUserInput = {
  formEntity: CreateExternalUserFormEntity
}

type SuccessResponse = {
  email: string
}
type ErrorResponse = {
  email: string
  errors: ErrorEntity[]
}

type CreateUsersOutput = {
  successes: SuccessResponse[]
  errors: ErrorResponse[]
}

type AssignUserProjectsInput = {
  userId: number
  isExternal: boolean
  formEntity: UserAssignProjectFormEntity
  compassRoles: CompassRoleEntity[]
}

type AssignUsersProjectsInput = {
  users: { id: number; isExternal: boolean }[]
  projectIds: string[]
  isSendAssignEmail: boolean
  compassRoleId: number
}

type UpdateNameInput = {
  userId: number
  updatedName: string
}

type UpdateKeywordsInput = {
  userId: number
  updatedKeywords: string[]
}

type UpdateCustomRolesInput = {
  userId: number
  customRoleIds: number[]
}
type IsUserSearchInput = {
  user: UserEntity
  target: string
}
type SearchUsersInput<T extends UserEntity> = {
  users: T[]
  searchWord: string
}

export const useUser = () => {
  const [addUserMutation] = useAddUserMutation()
  const [updateWorksRoleMutation] = useUpdateWorksRoleMutation()
  const [deleteUserMutation] = useDeleteUserMutation()
  const [updateInternalUserNameMutation] = useUpdateInternalUserNameMutation()
  const [updateUserKeywordsMutation] = useUpdateUserKeywordsMutation()
  const [updateUserCompanyRolesMutation] = useUpdateUserCompanyRolesMutation()

  const [addExternalUserMutation] = useAddExternalUserMutation()

  const [assignUserProjectsMutation] = useAssignUserProjectsMutation()
  const [assignUsersProjectsMutation] = useAssignUsersProjectsMutation()

  const { toErrorEntities } = useError()

  const createUsers = async ({ formEntity }: CreateUserInput): Promise<CreateUsersOutput> => {
    const inputs = UserTranslator.toCreateUserRequests(formEntity)

    const successes: SuccessResponse[] = []
    const errors: ErrorResponse[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (const input of inputs) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await addUserMutation({ variables: { input } })
        successes.push({ email: input.email })
      } catch (error) {
        const apolloErrors = toErrorEntities(error)
        errors.push({ email: input.email, errors: apolloErrors })
      }
    }

    return { successes, errors }
  }

  const updateRole = async ({ userId, roleId }: UpdateRoleInput): Promise<void> => {
    const input = UserTranslator.toUpdateWorksRoleRequest({ userId, roleId })
    await updateWorksRoleMutation({ variables: { input } })
  }

  const deleteUser = async ({ userId }: DeleteUserInput) => {
    await deleteUserMutation({ variables: { input: userId } })
  }

  const createExternalUsers = async ({ formEntity }: CreateExternalUserInput) => {
    const inputs = UserTranslator.toCreateExternalUserRequests(formEntity)

    const successes: SuccessResponse[] = []
    const errors: ErrorResponse[] = []

    // eslint-disable-next-line no-restricted-syntax
    for (const input of inputs) {
      try {
        // eslint-disable-next-line no-await-in-loop
        await addExternalUserMutation({ variables: { input } })
        successes.push({ email: input.email })
      } catch (error) {
        const apolloErrors = toErrorEntities(error)
        errors.push({ email: input.email, errors: apolloErrors })
      }
    }

    return { successes, errors }
  }

  const assignUserProjects = async ({
    userId,
    isExternal,
    formEntity,
    compassRoles,
  }: AssignUserProjectsInput) => {
    // 削除したものは含めないことでアサインを解除した事にする
    const targetFormEntity: UserAssignProjectFormEntity = {
      ...formEntity,
      projects: formEntity.projects.filter(project => !project.isDeleting),
    }

    const input = UserTranslator.toAssignUserProjectsRequest({
      formEntity: targetFormEntity,
      isExternal,
      compassRoles,
    })
    await assignUserProjectsMutation({ variables: { userId, input } })
  }

  const assignUsersProjects = async (parameters: AssignUsersProjectsInput): Promise<void> => {
    const input = UserTranslator.toAssignUsersProjectsRequest(parameters)
    await assignUsersProjectsMutation({ variables: { input } })
  }

  const updateName = async ({ userId, updatedName }: UpdateNameInput): Promise<void> => {
    await updateInternalUserNameMutation({ variables: { userId, name: updatedName } })
  }

  const updateKeywords = async ({
    userId,
    updatedKeywords,
  }: UpdateKeywordsInput): Promise<void> => {
    const keywords = updatedKeywords.filter(keyword => keyword.trim() !== '')
    await updateUserKeywordsMutation({ variables: { userId, params: { keywords } } })
  }

  const updateUserCustomRoles = async ({
    userId,
    customRoleIds,
  }: UpdateCustomRolesInput): Promise<void> => {
    await updateUserCompanyRolesMutation({
      variables: { userId, params: { companyRoleIds: unique(customRoleIds) } },
    })
  }

  /** UserEntityに対してtargetの文字列を用いた曖昧検索 */
  const isUserFuzzySearch = ({ user, target }: IsUserSearchInput): boolean => {
    const word = target.toLowerCase()
    return (
      user.email.toLowerCase().includes(word) ||
      user.name?.toLowerCase().includes(word) ||
      user.keywords?.some(keyword => keyword.toLowerCase().includes(word))
    )
  }

  /** UserEntityに対してtargetの文字列を用いた完全一致検索 */
  const isUserExactSearch = ({ user, target }: IsUserSearchInput): boolean => {
    return (
      user.email === target ||
      user.name === target ||
      user.keywords?.some(keyword => keyword === target)
    )
  }

  const isInternalUser = (user: UserEntity): user is InternalUserEntity => {
    return 'customRoles' in user && 'isResetPassword' in user
  }

  const searchUsers = <T extends UserEntity>({ users, searchWord }: SearchUsersInput<T>): T[] => {
    const searchWords = searchWord.split(/[\s,]/).filter(Boolean)

    return users.filter(user => {
      // 検索ワードが1つの場合は曖昧検索
      if (searchWords.length === 1) {
        const word = searchWords[0]
        const condition = isUserFuzzySearch({ user, target: word })

        // 社内ユーザーの場合は追加でカスタムロールも検索
        return isInternalUser(user)
          ? condition ||
              user.customRoles?.some(role => role.name.toLowerCase().includes(word.toLowerCase()))
          : condition
      }

      // 検索ワードが複数の場合は複数の文字それぞれが完全一致するよう検索
      return searchWords.some(word => {
        const condition = isUserExactSearch({ user, target: word })

        // 社内ユーザーの場合は追加でカスタムロールも検索
        return isInternalUser(user)
          ? condition || user.customRoles?.some(role => role.name === word)
          : condition
      })
    })
  }

  return {
    createUsers,
    updateRole,
    deleteUser,
    createExternalUsers,
    assignUserProjects,
    assignUsersProjects,
    updateName,
    updateKeywords,
    updateUserCustomRoles,
    isInternalUser,
    searchUsers,
  }
}
